import { useEffect, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { MessageBody } from './messages/registry';
import MessageReactions from './MessageReactions';
import { useChat } from '../../chat/ChatProvider';
import { canManagePins } from '../../chat/pinPermissions';

export default function MessageTimeline({ conversationId }) {
  const {
    state,
    myMemberId,
    loadOlderMessages,
    scheduleMarkRead,
    revokeMessage,
    retryFailed,
    pinMessage,
    unpinMessage,
    toggleFavorite,
    dispatch,
  } = useChat();
  const scrollerRef = useRef(null);
  const stickToBottom = useRef(true);
  const highlightId = state.highlightByConversation?.[String(conversationId)] || null;

  const timeline = state.messagesByConversation[String(conversationId)] || {
    items: [],
    loading: false,
    hasMoreBefore: false,
  };
  const optimistic = Object.values(state.optimisticMessages[String(conversationId)] || {});
  const failed = Object.values(state.failedMessages[String(conversationId)] || {});
  const pins = state.pinsByConversation[String(conversationId)] || [];
  const pinnedIds = useMemo(
    () => new Set(pins.map((p) => String(p.serverMessageId))),
    [pins]
  );
  const favoriteIds = useMemo(
    () => new Set((state.favoriteIdsByConversation[String(conversationId)] || []).map(String)),
    [state.favoriteIdsByConversation, conversationId]
  );
  const rows = useMemo(() => {
    const map = new Map();
    [...(timeline.items || []), ...optimistic, ...failed].forEach((m) => {
      const key = m.serverMessageId || m.clientMessageId;
      if (!key) return;
      if (m.serverMessageId && map.has(`c:${m.clientMessageId}`)) {
        map.delete(`c:${m.clientMessageId}`);
      }
      map.set(m.serverMessageId ? `s:${m.serverMessageId}` : `c:${m.clientMessageId}`, m);
    });
    return [...map.values()].sort((a, b) => {
      const sa = a.seq == null ? Number.MAX_SAFE_INTEGER : a.seq;
      const sb = b.seq == null ? Number.MAX_SAFE_INTEGER : b.seq;
      return sa - sb;
    });
  }, [timeline.items, optimistic, failed]);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el || !stickToBottom.current) return;
    el.scrollTop = el.scrollHeight;
  }, [rows.length, conversationId]);

  useEffect(() => {
    if (!highlightId || !scrollerRef.current) return;
    stickToBottom.current = false;
    const node = scrollerRef.current.querySelector(
      `[data-server-message-id="${CSS.escape(String(highlightId))}"]`
    );
    if (node) {
      node.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
  }, [highlightId, rows.length, conversationId]);

  useEffect(() => {
    if (!rows.length) return;
    const last = rows[rows.length - 1];
    if (last?.seq != null && document.visibilityState === 'visible') {
      scheduleMarkRead(conversationId, last.seq);
    }
  }, [rows, conversationId, scheduleMarkRead]);

  const onScroll = () => {
    const el = scrollerRef.current;
    if (!el) return;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    stickToBottom.current = nearBottom;
    if (el.scrollTop < 48 && timeline.hasMoreBefore && !timeline.loadingMore) {
      const prevHeight = el.scrollHeight;
      loadOlderMessages(conversationId).then(() => {
        requestAnimationFrame(() => {
          if (scrollerRef.current) {
            scrollerRef.current.scrollTop =
              scrollerRef.current.scrollHeight - prevHeight + el.scrollTop;
          }
        });
      });
    }
  };

  const summaries = state.participantSummaries;
  const readState = state.readStates[String(conversationId)];
  const conv = state.conversations.find(
    (c) => String(c.conversationId || c.id) === String(conversationId)
  );
  const managePins = canManagePins(conv);

  if (timeline.loading && !rows.length) {
    return <div className="chat-state">載入訊息…</div>;
  }
  if (timeline.error && !rows.length) {
    return <div className="chat-state chat-state--error">{timeline.error}</div>;
  }
  if (!rows.length) {
    return <div className="chat-state">開始對話吧</div>;
  }

  return (
    <div className="chat-timeline" ref={scrollerRef} onScroll={onScroll}>
      {timeline.loadingMore ? <div className="chat-timeline__more">載入更早訊息…</div> : null}
      {timeline.hasMoreBefore ? null : <div className="chat-timeline__end">已到最早訊息</div>}
      {rows.map((msg) => {
        const mine = String(msg.senderMemberId) === String(myMemberId);
        const name =
          summaries[msg.senderMemberId]?.displayName ||
          (mine ? '我' : `用戶 ${msg.senderMemberId}`);
        const peerRead = peerReadHint(conv, readState, msg, myMemberId);
        return (
          <article
            key={msg.serverMessageId || msg.clientMessageId}
            data-server-message-id={msg.serverMessageId || undefined}
            className={`chat-bubble ${mine ? 'is-mine' : 'is-peer'} ${
              msg._localStatus === 'failed' ? 'is-failed' : ''
            } ${msg._localStatus === 'sending' ? 'is-sending' : ''} ${
              highlightId && msg.serverMessageId === highlightId ? 'is-highlight' : ''
            }`}
          >
            {!mine ? <header className="chat-bubble__name">{name}</header> : null}
            {msg.replyTo ? (
              <div className="chat-bubble__reply">
                <small>回覆 {msg.replyTo.senderMemberId}</small>
                <span>{msg.replyTo.previewText || msg.replyTo.contentPreview || '…'}</span>
              </div>
            ) : null}
            <div className="chat-bubble__body">
              <MessageBody message={msg} />
              {msg.editedAt && msg.status !== 'REVOKED' ? (
                <span className="chat-bubble__edited">已編輯</span>
              ) : null}
            </div>
            <MessageReactions conversationId={conversationId} message={msg} />
            <footer className="chat-bubble__foot">
              <time>{formatTime(msg.createdAt)}</time>
              {msg._localStatus === 'sending' ? <span>傳送中</span> : null}
              {msg._localStatus === 'failed' ? (
                <button
                  type="button"
                  className="chat-link-btn"
                  onClick={() => retryFailed(conversationId, msg.clientMessageId)}
                >
                  重試
                </button>
              ) : null}
              {mine &&
              msg.serverMessageId &&
              msg.status !== 'REVOKED' &&
              msg.messageType === 'TEXT' ? (
                <button
                  type="button"
                  className="chat-link-btn"
                  onClick={() =>
                    dispatch({
                      type: 'EDIT_SET',
                      conversationId,
                      editTarget: {
                        serverMessageId: msg.serverMessageId,
                        text: msg.text || '',
                        mentionMemberIds: (msg.mentions || [])
                          .map((m) => m.memberId)
                          .filter((id) => id != null),
                      },
                    })
                  }
                >
                  編輯
                </button>
              ) : null}
              {mine && msg.serverMessageId && msg.status !== 'REVOKED' ? (
                <button
                  type="button"
                  className="chat-link-btn"
                  onClick={() => {
                    if (window.confirm('撤回這則訊息？')) {
                      revokeMessage(conversationId, msg.serverMessageId).catch(() => {});
                    }
                  }}
                >
                  撤回
                </button>
              ) : null}
              {msg.serverMessageId && msg.status !== 'REVOKED' ? (
                <button
                  type="button"
                  className="chat-link-btn"
                  onClick={() =>
                    dispatch({
                      type: 'REPLY_SET',
                      conversationId,
                      replyTo: {
                        serverMessageId: msg.serverMessageId,
                        senderMemberId: msg.senderMemberId,
                        previewText: msg.text || msg.emoteCode || msg.fileName || msg.messageType,
                      },
                    })
                  }
                >
                  回覆
                </button>
              ) : null}
              {managePins && msg.serverMessageId && msg.status !== 'REVOKED' ? (
                <button
                  type="button"
                  className="chat-link-btn"
                  onClick={() => {
                    const id = msg.serverMessageId;
                    const action = pinnedIds.has(String(id))
                      ? unpinMessage(conversationId, id)
                      : pinMessage(conversationId, id);
                    action.catch(() => {});
                  }}
                >
                  {pinnedIds.has(String(msg.serverMessageId)) ? '取消置頂' : '置頂'}
                </button>
              ) : null}
              {msg.serverMessageId && msg.status !== 'REVOKED' ? (
                <button
                  type="button"
                  className={`chat-link-btn${
                    favoriteIds.has(String(msg.serverMessageId)) ? ' is-favorited' : ''
                  }`}
                  onClick={() => {
                    const id = msg.serverMessageId;
                    const on = favoriteIds.has(String(id));
                    toggleFavorite(conversationId, id, on).catch(() => {});
                  }}
                >
                  {favoriteIds.has(String(msg.serverMessageId)) ? '已收藏' : '收藏'}
                </button>
              ) : null}
              {!mine && msg.serverMessageId && msg.status !== 'REVOKED' ? (
                <Link
                  to={`/chat/report?memberId=${encodeURIComponent(msg.senderMemberId)}&conversationId=${encodeURIComponent(conversationId)}&serverMessageId=${encodeURIComponent(msg.serverMessageId)}`}
                  className="chat-link-btn"
                >
                  檢舉
                </Link>
              ) : null}
              {peerRead ? <span className="chat-bubble__read">{peerRead}</span> : null}
            </footer>
          </article>
        );
      })}
    </div>
  );
}

function peerReadHint(conv, readState, msg, myMemberId) {
  if (!msg?.seq || String(msg.senderMemberId) !== String(myMemberId)) return '';
  if (conv?.conversationType === 'DIRECT' && conv.peerMemberId) {
    const peer = (readState?.participants || []).find(
      (p) => String(p.memberId) === String(conv.peerMemberId)
    );
    if (peer && peer.lastReadSeq >= msg.seq) return '已讀';
    return '';
  }
  if (conv?.conversationType === 'GROUP' || conv?.conversationType === 'BUSINESS_GROUP') {
    const count = (readState?.participants || []).filter(
      (p) =>
        String(p.memberId) !== String(myMemberId) && (p.lastReadSeq || 0) >= msg.seq
    ).length;
    if (count > 0) return `${count} 人已讀`;
  }
  return '';
}

function formatTime(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}
